import React, { useState } from 'react';
import {
    AansluitkastUnitBasisgegevens,
    AansluitkastUnitData,
} from '../../../../../common/src/domain/customInspectionTypes/amsterdamOverspanningen/inspectieOverspanningen';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelCell,
    EditPanelCellLabel,
    EditPanelInput,
    EditPanelRow,
    PanelBody,
    PanelButtonLink,
    Tray,
} from '../../../components/elements';
import { ISurveyTemplateUnitData } from '../../../components/Surveys/ISurveyTemplateUnitData';
import { EditPanelInputNumber } from '../../../components/utils/editPanel/EditPanelInputNumber';
import { EditPanelTrueFalseUndefined } from '../../../components/utils/editPanel/EditPanelTrueFalseUndefined';
import { useTranslate } from '../../../mixins/translate/translate';
import { BasisgegevensValue } from './components/BasisgegevensValue';
import { EditPanelStraatBagSelect } from './EditPanelStraatBagSelect';
import { EditPanelYesNoEmptyRow } from './EditPanelYesNoEmptyRow';
import { MapDialog } from './MapDialog';
import { SurveyEditPhotosPanel } from './SurveyEditPhotosPanel';
import { useInspectieOverspanningenState } from './useInspectieOverspanningenState';

export type MapDialogState = {
    selectedLatitude?: number | undefined;
    selectedLongitude?: number | undefined;
    initialLatitude?: number | undefined;
    initialLongitude?: number | undefined;
};

export function SurveyEditAoInspectieOverspanningenUnitAansluitkast(
    props: { surveyId: string; objectId: string; onItemChanged: () => Promise<void> } & ISurveyTemplateUnitData,
) {
    const translate = useTranslate();
    const [isMapVisible, setIsMapVisible] = useState(false);
    const {
        basisgegevensState,
        formState,
        save: saveDatabase,
        setFromState,
    } = useInspectieOverspanningenState<AansluitkastUnitBasisgegevens, AansluitkastUnitData>(props.surveyId, props.unitId, props.objectId);

    async function save(data: AansluitkastUnitData) {
        await saveDatabase(data);
        await props.onItemChanged();
    }

    async function onChange(name: keyof AansluitkastUnitData, value: any, shouldSave: boolean) {
        const newFormState = { ...formState, [name]: value };

        setFromState(newFormState);
        if (shouldSave) {
            await save(newFormState);
        }
    }
    async function onBlur() {
        if (!formState) {
            throw new Error('formState is not set');
        }
        await save(formState);
    }

    async function onMapClose(newValue?: { latitude: number; longitude: number }) {
        setIsMapVisible(false);
        if (newValue) {
            const newFormState = {
                ...formState,
                latitudeMutatie: newValue.latitude,
                longitudeMutatie: newValue.longitude,
            };
            setFromState(newFormState);
            await save(newFormState);
        }
    }

    if (formState === undefined) {
        return null;
    }

    return (
        <>
            {isMapVisible && (
                <MapDialog
                    onClose={onMapClose}
                    latitude={formState.latitudeMutatie ?? basisgegevensState.latitude}
                    longitude={formState.longitudeMutatie ?? basisgegevensState.longitude}
                />
            )}
            <Tray>
                <EditPanel bottomMargin={true} noPadding={true}>
                    <CollapsiblePanel heading={{ dark: true, text: `${props.unitName} inventarisatie`, initialIsClosed: false }}>
                        <PanelBody isForm={true}>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Straat (BAG)"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <BasisgegevensValue value={basisgegevensState.straatbag} />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={2}></EditPanelCell>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelStraatBagSelect
                                        onChange={(value) => void onChange('straatBagMutatie', value ?? undefined, true)}
                                        value={formState.straatBagMutatie}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Lichtpuntnummer"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <BasisgegevensValue value={basisgegevensState.lichtpuntnummer} />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={2}></EditPanelCell>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelInput
                                        name="lichtpuntnummerMutatie"
                                        value={formState.lichtpuntnummerMutatie}
                                        handleChange={async (value) => onChange('lichtpuntnummerMutatie', value, false)}
                                        handleBlur={onBlur}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Stijgbuis zichtbaar"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelTrueFalseUndefined
                                        size="large"
                                        undefinedText="Leeg"
                                        name="stijgbuisZichtbaarMutatie"
                                        value={formState.stijgbuisZichtbaarMutatie}
                                        onChange={async (value) => onChange('stijgbuisZichtbaarMutatie', value, false)}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Coördinaten"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    {basisgegevensState.latitude ? (
                                        <>
                                            {basisgegevensState.latitude} / {basisgegevensState.longitude}
                                        </>
                                    ) : (
                                        <i>Waarde onbekend</i>
                                    )}
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCell rowCount={2}></EditPanelCell>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        name="latitudeMutatie"
                                        value={formState.latitudeMutatie}
                                        onChange={async (value) => onChange('latitudeMutatie', value, false)}
                                        onBlur={onBlur}
                                    />
                                </EditPanelCell>
                                <EditPanelCell rowCount={2}>
                                    <EditPanelInputNumber
                                        name="longitudeMutatie"
                                        value={formState.longitudeMutatie}
                                        onChange={async (value) => onChange('longitudeMutatie', value, false)}
                                        onBlur={onBlur}
                                    />
                                </EditPanelCell>
                                <EditPanelCell rowCount={4}>
                                    <PanelButtonLink
                                        text={translate('object.selectPoint')}
                                        onClick={() => setIsMapVisible(true)}
                                        small
                                        icon="fa fa-globe"
                                    ></PanelButtonLink>
                                </EditPanelCell>
                            </EditPanelRow>
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={2} text="Opmerking"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelInput
                                        name="opmerkingMutatie"
                                        value={formState.opmerkingMutatie}
                                        handleChange={async (value) => onChange('opmerkingMutatie', value, false)}
                                        handleBlur={onBlur}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        </PanelBody>
                    </CollapsiblePanel>

                    <CollapsiblePanel heading={{ dark: true, text: `${props.unitName} inspectie` }}>
                        <PanelBody isForm={true}>
                            <EditPanelYesNoEmptyRow
                                label="Schade aan aansluitkabel?"
                                name="schadeAanAansluitkabel"
                                value={formState.schadeAanAansluitkabel}
                                onChange={async (value) => onChange('schadeAanAansluitkabel', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Onjuiste montage aan spandraad?"
                                name="onjuisteMontageAanSpandraad"
                                value={formState.onjuisteMontageAanSpandraad}
                                onChange={async (value) => onChange('onjuisteMontageAanSpandraad', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Onjuiste montage aan gevel?"
                                name="onjuisteMontageAanGevel"
                                value={formState.onjuisteMontageAanGevel}
                                onChange={async (value) => onChange('onjuisteMontageAanGevel', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Schade aan aansluitkast?"
                                name="schadeAanAansluitkast"
                                value={formState.schadeAanAansluitkast}
                                onChange={async (value) => onChange('schadeAanAansluitkast', value, true)}
                            />
                            <EditPanelYesNoEmptyRow
                                label="Sticker met identificatienummer onbruikbaar/onleesbaar?"
                                name="stickerMetIdentificatienummerOnbruikbaarOnleesbaar"
                                value={formState.stickerMetIdentificatienummerOnbruikbaarOnleesbaar}
                                onChange={async (value) => onChange('stickerMetIdentificatienummerOnbruikbaarOnleesbaar', value, true)}
                            />
                            <EditPanelRow>
                                <EditPanelCellLabel rowCount={4} text="Opmerking"></EditPanelCellLabel>
                                <EditPanelCell rowCount={8}>
                                    <EditPanelInput
                                        handleBlur={onBlur}
                                        name="opmerking"
                                        value={formState.opmerking}
                                        handleChange={async (value) => onChange('opmerking', value, false)}
                                    />
                                </EditPanelCell>
                            </EditPanelRow>
                        </PanelBody>
                    </CollapsiblePanel>

                    <SurveyEditPhotosPanel
                        objectId={props.objectId}
                        surveyId={props.surveyId}
                        unitName={props.unitName}
                        photos={formState.fotoBestanden}
                        onPhotosChanged={async (photos) => onChange('fotoBestanden', photos, true)}
                    />
                </EditPanel>
            </Tray>
        </>
    );
}
